import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { AspirationSelectButton } from '../components/AspirationSelectButton';

export const useColumnDefs = (): ColDef[] => {
  const { t } = useTranslation();
  const aspirationSelectColumnStayAtFront = useFeatureFlag(
    'Aspiration_Select_Column_Stay_At_Front',
  );
  const autoSortByAspirationStatus = useFeatureFlag(
    'Auto_Sort_By_Aspiration_Status',
  );
  return [
    {
      cellRenderer: AspirationSelectButton,
      cellClassRules: {
        glass: (params) => params.data?.completeOrder > 0,
      },
      width: 90,
      suppressMovable: aspirationSelectColumnStayAtFront,
      lockPosition: aspirationSelectColumnStayAtFront,
    },
    {
      field: 'opuNum',
      headerName: t('aspiration.opuNum'),
      width: 100,
      valueGetter: (params) => {
        const v = params.data?.opuNum;
        return v == null || v === '' ? null : Number(v);
      },
      comparator: (a, b) => {
        if (a == null) return -1;
        if (b == null) return 1;
        return a - b;
      },
      sort: 'asc',
      ...(autoSortByAspirationStatus && { sortIndex: 1 }),
      filter: 'agNumberColumnFilter',
    },
    {
      field: 'aspirationTime',
      headerName: t('aspiration.aspirationTime'),
      width: 100,
    },
    { field: 'account', headerName: t('aspiration.account'), width: 100 },
    { field: 'csa', headerName: t('aspiration.csa'), width: 100 },
    { field: 'breed', headerName: t('aspiration.breed'), width: 100 },
    { field: 'donor', headerName: t('aspiration.donor'), width: 200 },
    { field: 'sire', headerName: t('aspiration.sire'), width: 100 },
    { field: 'semenType', headerName: t('aspiration.semenType'), width: 100 },
    {
      field: 'donorLocation',
      headerName: t('aspiration.donorLocation'),
      width: 100,
    },
    {
      field: 'programName',
      headerName: t('aspiration.programName'),
      width: 150,
    },
    { field: 'pooled', headerName: t('aspiration.pooled'), width: 100 },
    { field: 'export', headerName: t('aspiration.export'), width: 100 },
    {
      field: 'nonEuComment',
      headerName: t('aspiration.nonEuComment'),
      width: 100,
    },
    { field: 'medTotal', headerName: t('aspiration.medTotal'), width: 100 },
    {
      field: 'totalOocytes',
      headerName: t('aspiration.totalOocytes'),
      width: 100,
    },
    { field: 'numInMat', headerName: t('aspiration.numInMat'), width: 100 },
    { field: 'csr', headerName: t('aspiration.csr'), width: 100 },
    { field: 'matTime', headerName: t('aspiration.matTime'), width: 100 },
    {
      field: 'aspComments',
      headerName: t('aspiration.aspirationComments'),
      width: 400,
    },
    { field: 'poolNum', headerName: t('aspiration.poolNum'), width: 100 },
    {
      field: 'dsAspirationComment',
      headerName: t('aspiration.dsAspirationComment'),
      width: 100,
    },
    { field: 'weight', headerName: t('aspiration.weight'), width: 100 },
    {
      field: 'completeOrder',
      hide: true,
      sortable: true,
      sort: 'asc',
      ...(autoSortByAspirationStatus && { sortIndex: 0 }),
    },
  ];
};

export const useEmbGridColumnDefs = (): ColDef[] => {
  const { t } = useTranslation();
  return [
    { field: 'aspDate', headerName: t('aspiration.date'), width: 100 },
    { field: 'tech', headerName: t('aspiration.tech'), width: 100 },
    { field: 'sched', headerName: t('aspiration.sched'), width: 80 },
    { field: 'fsh', headerName: t('aspiration.fsh'), width: 80 },
    { field: 'follS', headerName: t('aspiration.follS'), width: 50 },
    { field: 'follM', headerName: t('aspiration.follM'), width: 50 },
    { field: 'follL', headerName: t('aspiration.follL'), width: 50 },
    { field: 'ooc', headerName: t('aspiration.ooc'), width: 80 },
    { field: 'clls', headerName: t('aspiration.clls'), width: 80 },
    { field: 'clrs', headerName: t('aspiration.clrs'), width: 80 },
    { field: 'cystsL', headerName: t('aspiration.cystsL'), width: 80 },
    { field: 'cystsR', headerName: t('aspiration.cystsR'), width: 80 },
    { field: 'sire', headerName: t('aspiration.sirePool'), width: 120 },
    { field: 'fert', headerName: t('aspiration.fert'), width: 90 },
    { field: 'clv', headerName: t('aspiration.cleaved'), width: 100 },
    { field: 'viable', headerName: t('aspiration.viable'), width: 90 },
  ];
};

export const useProceduresColDefs = (): ColDef[] => {
  const { t } = useTranslation();
  return [
    {
      field: 'procedureDate',
      headerName: t('aspiration.procedureDate'),
      width: 120,
    },
    { field: 'procedure', headerName: t('aspiration.procedure'), width: 120 },
    {
      field: 'description',
      headerName: t('aspiration.description'),
      width: 400,
    },
    { field: 'pregnant', headerName: t('aspiration.pregnant'), width: 100 },
    { field: 'procCode', headerName: t('aspiration.procCode'), width: 100 },
  ];
};

export const useAspirationHistoryColDefs = (): ColDef[] => {
  const { t } = useTranslation();
  return [
    {
      field: 'aspirationDate',
      headerName: t('aspiration.aspirationDate'),
      width: 120,
    },
    {
      field: 'comments',
      headerName: t('aspiration.comment'),
      width: 700,
    },
  ];
};

export const useDonorCommentsColDefs = (): ColDef[] => {
  const { t } = useTranslation();
  return [
    {
      headerName: t('aspiration.aspirationComment'),
      field: 'aspirationComment',
      width: 300,
    },
    {
      headerName: t('aspiration.donorComment'),
      field: 'donorComment',
      minWidth: 200,
    },
    { headerName: t('aspiration.ptComment'), field: 'ptComment', width: 300 },
  ];
};
